import { Flex } from '@chakra-ui/react';
import { IGNORE_TARGET_CLASS } from 'constants/bounding-box';
import { store, useAppSelector } from 'modules/Editor/store';
import {
  selectForceHideWidgetControl,
  selectIsFlowModeEnabled,
  selectIsScrollZooming,
  selectZoom,
} from 'modules/Editor/store/editorSettingsSelector';
import { selectWidget, selectWidgetProperty } from 'modules/Editor/store/infographSelector';
import { WidgetActionState } from 'modules/Editor/store/widgetControlSlice';
import {
  selectActiveWidgetType,
  selectActiveWidgets,
  selectCropId,
  selectWidgetActionState,
} from 'modules/Editor/store/widgetSelector';
import { useEffect, useMemo, useRef } from 'react';
import { calculateWidgetBoundingBox } from 'utils/dom';
import { AllWidgetData } from 'widgets/Widget.types';
import { getBoundingRectOfRotatedWidget } from 'widgets/WidgetLayout.helpers';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

import { WIDGET_CONTROL_COMPONENT_MAP } from './WidgetCustomControl.map';
import { DuplicateControl } from './controls/DuplicateControl';
import { GroupControl } from './controls/GroupControl';
import { LockControl } from './controls/LockControl';

/**
 * Resets position of el to 0, 0, with 0x0 dimensions
 * @param el
 * @returns
 */
const resetPosition = (el?: HTMLDivElement) => {
  if (!el) return;

  el.style.left = '0px';
  el.style.top = '0px';
  el.style.width = '0px';
  el.style.height = '0px';
};

// Note: this is the padding for flowchart, update if need to add for all widgets
const PADDING_FROM_WIDGET = 30;
const PADDING_FROM_CUSTOM_WIDGET = 12;

// Padding for widget control if placed at bottom and rotate/drag handles are displayed
const PADDING_WITH_ROTATE_CTRL = 60;
const PADDING_CUSTOM_WITH_ROTATE_CTRL = 46;

/**
 * WIDGET CONTROL COMPONENT
 * Renders in-context menu above/below the active widget
 */
export const WidgetControl = () => {
  const activeWidgets = useAppSelector(selectActiveWidgets);
  const hasOneActiveWidget = activeWidgets.length === 1;
  const widgetActionState = useAppSelector(selectWidgetActionState);
  const forceHideWidgetControl = useAppSelector(selectForceHideWidgetControl);
  const cropId = useAppSelector(selectCropId);
  const isScrollZooming = useAppSelector(selectIsScrollZooming);
  const isFlowModeEnabled = useAppSelector(selectIsFlowModeEnabled);

  // Get widget type to render custom widget control
  const widgetType = useAppSelector(selectActiveWidgetType);
  const CustomWidgetControl = widgetType ? WIDGET_CONTROL_COMPONENT_MAP[widgetType] : null;
  const hasCustomWidgetControl = !!CustomWidgetControl;

  // Show widget control for flow mode widget, single-selection only
  const showWidgetControl = useMemo(() => {
    if (forceHideWidgetControl) return false;
    if (!hasOneActiveWidget) return false;
    if (isScrollZooming) return false;

    // Image in crop mode
    if (!!cropId) return false;

    if (widgetActionState !== WidgetActionState.Default) return false;

    // Flow mode is enabled - show widget control, skip line connection check
    if (isFlowModeEnabled) return true;

    const { id, groupId, responsiveGroupId } = activeWidgets[0];
    const isFlowWidget = lineConnectionCache.isConnected(groupId || responsiveGroupId || id);
    if (!isFlowWidget && !hasCustomWidgetControl) return false;

    return true;
  }, [
    activeWidgets,
    hasOneActiveWidget,
    isScrollZooming,
    cropId,
    widgetActionState,
    forceHideWidgetControl,
    isFlowModeEnabled,
    hasCustomWidgetControl,
  ]);

  if (!showWidgetControl) {
    return null;
  }

  return <WidgetControlContent />;
};

/**
 * Widget Control Content
 * Renders general widget action buttons above/below the current active widget
 */
const WidgetControlContent = () => {
  /** Refs */
  const widgetControlRef = useRef<HTMLDivElement>(null);
  const widgetControlContentRef = useRef<HTMLDivElement>(null);

  /** Store selectors */
  const activeWidgets = useAppSelector(selectActiveWidgets);
  const zoom = useAppSelector(selectZoom);
  const { id, groupId, responsiveGroupId } = activeWidgets[0];
  const isFlowModeEnabled = useAppSelector(selectIsFlowModeEnabled);
  const isFlowWidget = lineConnectionCache.isConnected(groupId || responsiveGroupId || id);

  const showDefaultContextMenu = useMemo(() => {
    return isFlowModeEnabled || isFlowWidget;
  }, [isFlowModeEnabled, isFlowWidget]);

  // Get position of active widget - need to listen to changes in position caused by external updates (not moveable)
  // i.e. undo/redo, alignment, etc.
  const activeWidgetId = activeWidgets[0]?.id;
  const leftPx = useAppSelector(selectWidgetProperty<AllWidgetData, 'leftPx'>(activeWidgetId || '', 'leftPx'));
  const topPx = useAppSelector(selectWidgetProperty<AllWidgetData, 'topPx'>(activeWidgetId || '', 'topPx'));
  const widthPx = useAppSelector(selectWidgetProperty<AllWidgetData, 'widthPx'>(activeWidgetId || '', 'widthPx'));
  const heightPx = useAppSelector(selectWidgetProperty<AllWidgetData, 'heightPx'>(activeWidgetId || '', 'heightPx'));

  // Get widget type to render custom widget control
  const widgetType = useAppSelector(selectActiveWidgetType);
  const CustomWidgetControl = widgetType ? WIDGET_CONTROL_COMPONENT_MAP[widgetType] : null;
  const hasCustomWidgetControl = !!CustomWidgetControl;

  // Set the widget control position to the active widget position
  useEffect(() => {
    if (!widgetControlRef.current) {
      return;
    }

    const activeWidget = activeWidgets[0];
    if (!activeWidget) {
      resetPosition(widgetControlRef.current);
      return;
    }

    // Use setTimeout to ensure widget is rendered before calculating position
    // i.e. for undo/redo or external updates to position not caused by moveable
    setTimeout(() => {
      const { groupId, responsiveGroupId, id: activeWidgetId } = activeWidget;
      const widgetData = selectWidget(groupId || responsiveGroupId || activeWidgetId)(store.getState());
      const widgetPos = calculateWidgetBoundingBox({
        widgetId: activeWidgetId,
        groupId,
        responsiveGroupId,
        widgetData,
      });
      if (!widgetPos) return;
      const { leftPx, topPx, widthPx, heightPx, rotateDeg } = widgetPos;

      // For rotated widgets, get bounding rect considering rotation
      const rotatedBoundingRect = getBoundingRectOfRotatedWidget(leftPx, topPx, widthPx, heightPx, rotateDeg);

      // Update position of widget ctrl box
      const widgetCtrlBoxEl = widgetControlRef.current;
      if (!widgetCtrlBoxEl) return;
      widgetCtrlBoxEl.style.left = `${rotatedBoundingRect.leftPx}px`;
      widgetCtrlBoxEl.style.top = `${rotatedBoundingRect.topPx}px`;
      widgetCtrlBoxEl.style.width = `${rotatedBoundingRect.widthPx}px`;
      widgetCtrlBoxEl.style.height = `${rotatedBoundingRect.heightPx}px`;

      // Position buttons above widget, if no space on page, position on bottom
      if (!widgetControlContentRef.current) return;
      widgetControlContentRef.current.style.display = 'flex';
      if (
        (showDefaultContextMenu && rotatedBoundingRect.topPx - PADDING_FROM_WIDGET < PADDING_FROM_WIDGET) ||
        (!showDefaultContextMenu && rotatedBoundingRect.topPx - PADDING_FROM_CUSTOM_WIDGET < PADDING_FROM_CUSTOM_WIDGET)
      ) {
        // No space at top, position at bottom
        // Need to consider rotate/drag handles
        const isGroup = !!groupId;
        const isResponsiveGroup = !!responsiveGroupId;
        let padding = isGroup || isResponsiveGroup ? PADDING_FROM_WIDGET : PADDING_WITH_ROTATE_CTRL;
        const pCustom = isGroup || isResponsiveGroup ? PADDING_FROM_CUSTOM_WIDGET : PADDING_CUSTOM_WITH_ROTATE_CTRL;
        padding = showDefaultContextMenu ? padding : pCustom;
        widgetControlContentRef.current.style.top = `calc(100% + ${(padding * 1) / zoom}px)`;
        widgetControlContentRef.current.style.bottom = 'unset';
        widgetControlContentRef.current.style.transformOrigin = 'center top';
      } else {
        // Default, position at top
        const padding = showDefaultContextMenu ? PADDING_FROM_WIDGET : PADDING_FROM_CUSTOM_WIDGET;
        widgetControlContentRef.current.style.bottom = `calc(100% + ${(padding * 1) / zoom}px)`;
        widgetControlContentRef.current.style.top = 'unset';
        widgetControlContentRef.current.style.transformOrigin = 'center bottom';
      }
    }, 0);
  }, [activeWidgets, zoom, widthPx, heightPx, leftPx, topPx, showDefaultContextMenu]);

  return (
    <Flex
      zIndex='widgetControl'
      ref={widgetControlRef}
      w={0}
      h={0}
      position='absolute'
      justifyContent='center'
      alignItems='center'
      pointerEvents='none'
    >
      <Flex
        className={IGNORE_TARGET_CLASS}
        transform={`scale(${1 / zoom})`}
        ref={widgetControlContentRef}
        w='fit-content'
        h='fit-content'
        p={showDefaultContextMenu ? '2px' : 0}
        gap={'2px'}
        justifyContent='space-between'
        boxShadow='0px 0px 4px 0px #0000003D'
        bg='white'
        borderRadius='md'
        position='absolute'
        pointerEvents='auto'
        display='none'
      >
        {showDefaultContextMenu && <DuplicateControl />}
        {showDefaultContextMenu && <GroupControl />}
        {hasCustomWidgetControl && <CustomWidgetControl />}
        {showDefaultContextMenu && <LockControl />}
      </Flex>
    </Flex>
  );
};
